import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import AuthContext from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { FaEye, FaSearch, FaFilter, FaChevronDown, FaChevronUp } from "react-icons/fa";
import "./GerenciarRecursos.css";

const PAGE_SIZE = 15;

function GerenciarRecursos() {
  const { addToast } = useContext(ToastContext);
  const { user } = useContext(AuthContext);
  const basePath = Number(user?.perfil_id) === 1 ? "/admin/recursos" : "/controlador/recursos";
  const title = "Gerenciar Recursos";
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState([]);
  const [filters, setFilters] = useState({ termo: "", status: "" });
  const [expandedEditais, setExpandedEditais] = useState(() => new Set());
  const [resourcesByEdital, setResourcesByEdital] = useState({});

  const emptyMeta = useMemo(
    () => ({ page: 1, pageSize: PAGE_SIZE, total: 0, pageCount: 1 }),
    []
  );

  const fetchSummary = async (currentFilters = filters) => {
    setLoading(true);
    try {
      const { data } = await api.get("/recursos/controlador/summary", {
        params: {
          ...currentFilters,
        },
      });
      setSummary(data.data || []);
    } catch (error) {
      addToast("Erro ao buscar recursos.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchEditalResources = async (editalId, page = 1, currentFilters = filters) => {
    setResourcesByEdital((prev) => ({
      ...prev,
      [editalId]: {
        ...(prev[editalId] || { rows: [], meta: emptyMeta }),
        loading: true,
        error: null,
      },
    }));

    try {
      const { data } = await api.get("/recursos/controlador/list", {
        params: {
          ...currentFilters,
          editalId,
          page,
          pageSize: PAGE_SIZE,
        },
      });

      setResourcesByEdital((prev) => ({
        ...prev,
        [editalId]: {
          rows: data.data || [],
          meta: data.meta || emptyMeta,
          loading: false,
          error: null,
        },
      }));
    } catch (error) {
      setResourcesByEdital((prev) => ({
        ...prev,
        [editalId]: {
          ...(prev[editalId] || { rows: [], meta: emptyMeta }),
          loading: false,
          error: true,
        },
      }));
      addToast("Erro ao buscar recursos do edital.", "error");
    }
  };

  useEffect(() => {
    fetchSummary(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOptions = useMemo(() => {
    const base = ["Pendente", "Em Análise", "Deferido", "Indeferido"];
    return base;
  }, []);

  const statusClass = (status) => {
    switch (status) {
      case "Deferido":
        return "pill pill--success";
      case "Indeferido":
        return "pill pill--danger";
      case "Em Análise":
        return "pill pill--info";
      default:
        return "pill pill--muted";
    }
  };

  const applyFilters = () => {
    setExpandedEditais(new Set());
    setResourcesByEdital({});
    fetchSummary(filters);
  };

  const toggleEdital = (editalId) => {
    setExpandedEditais((prev) => {
      const next = new Set(prev);
      if (next.has(editalId)) {
        next.delete(editalId);
      } else {
        next.add(editalId);
      }
      return next;
    });

    if (!resourcesByEdital[editalId]) {
      fetchEditalResources(editalId, 1, filters);
    }
  };

  const goToEditalPage = (editalId, direction) => {
    const current = resourcesByEdital[editalId];
    const currentMeta = current?.meta || emptyMeta;
    const next = currentMeta.page + direction;
    if (next < 1 || next > currentMeta.pageCount) return;
    fetchEditalResources(editalId, next, filters);
  };

  return (
    <>
      <PageHeader title={title} />

      <div className="filters-bar surface-section" style={{ padding: 14 }}>
        <div className="gr-filters__search">
          <FaSearch className="gr-filters__icon" />
          <input
            type="search"
            value={filters.termo}
            placeholder="Buscar por candidato, CPF, edital, texto..."
            onChange={(e) => setFilters((p) => ({ ...p, termo: e.target.value }))}
          />
        </div>

        <div className="gr-filters__status">
          <FaFilter className="gr-filters__icon" />
          <select
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
          >
            <option value="">Todos os status</option>
            {statusOptions.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="spacer" />
        <button onClick={applyFilters}>Aplicar</button>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : summary.length ? (
        <>
          <div className="gr-grid stack-lg">
            {summary.map((e) => {
              const editalId = Number(e.edital_id);
              const isOpen = expandedEditais.has(editalId);
              const editalResources = resourcesByEdital[editalId] || {
                rows: [],
                meta: emptyMeta,
                loading: false,
                error: null,
              };

              return (
                <div key={editalId} className="gr-card gr-edital-card surface-section">
                  <div className="gr-card__header">
                    <div className="gr-edital__left">
                      <div className="gr-card__title">
                        <strong>
                          {e.numero_edital}/{e.ano_edital}
                        </strong>
                        {e.nome_edital ? (
                          <span className="muted-text">{e.nome_edital}</span>
                        ) : null}
                      </div>

                      <div className="gr-edital__badges">
                        <span className="pill pill--info">Total: {e.total}</span>
                        <span
                          className={
                            Number(e.pendentes) > 0 ? "pill pill--danger" : "pill pill--muted"
                          }
                          title="Não analisados = Pendente + Em Análise"
                        >
                          Não analisados: {e.pendentes}
                        </span>
                      </div>
                    </div>

                    <div className="gr-edital__right">
                      <div className="gr-card__meta muted-text">
                        {e.ultimo_recurso_em
                          ? `Último: ${new Date(e.ultimo_recurso_em).toLocaleString()}`
                          : ""}
                      </div>

                      <button
                        type="button"
                        className="btn-modern"
                        onClick={() => toggleEdital(editalId)}
                      >
                        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                        <span>{isOpen ? "Fechar recursos" : "Abrir recursos"}</span>
                      </button>
                    </div>
                  </div>

                  {isOpen ? (
                    <div className="gr-edital__expand">
                      {editalResources.loading ? (
                        <p>Carregando recursos...</p>
                      ) : editalResources.rows?.length ? (
                        <>
                          <div className="gr-recurso-list">
                            {editalResources.rows.map((r) => (
                              <div key={r.id} className="gr-recurso-item">
                                <div className="gr-recurso-item__main">
                                  <div className="gr-recurso-item__top">
                                    <strong>Recurso #{r.id}</strong>
                                    <span className={statusClass(r.status)}>{r.status}</span>
                                  </div>
                                  <div className="muted-text gr-recurso-item__sub">
                                    {r.nome_completo} {r.cpf ? `(${r.cpf})` : ""}
                                    {r.nome_cargo ? ` • ${r.nome_cargo}` : ""}
                                    {r.criado_em
                                      ? ` • ${new Date(r.criado_em).toLocaleString()}`
                                      : ""}
                                  </div>
                                </div>
                                <div className="gr-recurso-item__actions">
                                  <Link className="btn-modern" to={`${basePath}/${r.id}`}>
                                    <FaEye />
                                    <span>Abrir</span>
                                  </Link>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="gr-inner-pagination">
                            <button
                              className="button-secondary"
                              onClick={() => goToEditalPage(editalId, -1)}
                              disabled={editalResources.meta.page === 1}
                            >
                              Página anterior
                            </button>
                            <span>
                              Página {editalResources.meta.page} de {editalResources.meta.pageCount} (Total: {editalResources.meta.total})
                            </span>
                            <button
                              onClick={() => goToEditalPage(editalId, 1)}
                              disabled={editalResources.meta.page === editalResources.meta.pageCount}
                            >
                              Próxima página
                            </button>
                          </div>
                        </>
                      ) : editalResources.error ? (
                        <p>Erro ao carregar recursos do edital.</p>
                      ) : (
                        <p>Nenhum recurso encontrado para este edital.</p>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <p>Nenhum recurso encontrado.</p>
      )}
    </>
  );
}

export default GerenciarRecursos;
